import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * API client with automatic authentication handling.
 * 
 * SECURITY NOTE: This uses localStorage for auth tokens as a fallback for 
 * Safari iOS which blocks third-party cookies due to ITP (Intelligent Tracking Prevention).
 * The backend also accepts httpOnly cookies which are preferred when available.
 * 
 * Token is stored in localStorage because:
 * 1. Safari iOS blocks cookies after redirect from auth.emergentagent.com
 * 2. localStorage is not affected by ITP restrictions
 * 3. The token has a 7-day expiry and is validated server-side
 * 
 * Mitigations in place:
 * - All API requests use HTTPS
 * - Token is cleared on logout and 401 responses
 * - Backend validates tokens against database with expiry checks
 */

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Still try cookies first
  timeout: 15000
});

// Add request interceptor to include token from localStorage
api.interceptors.request.use(
  (config) => {
    // Add Authorization header if token exists in localStorage
    const token = localStorage.getItem('sorta_auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored token on auth error
      localStorage.removeItem('sorta_auth_token');
    }
    return Promise.reject(error);
  }
);

// Helper to set auth token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('sorta_auth_token', token);
  } else {
    localStorage.removeItem('sorta_auth_token');
  }
};

// Helper to clear auth
export const clearAuth = () => {
  localStorage.removeItem('sorta_auth_token');
};

// Helper to check if we have a stored token
export const hasStoredToken = () => {
  return !!localStorage.getItem('sorta_auth_token');
};

export default api;
